import moment from 'moment';
import Popper from 'popper.js';
import * as React from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { EditContext, EntityVersionType } from 'types';
import BaseDto from 'types/__models__/BaseDto';
import { formatDateTime } from 'util/Time';
import { ModelStatus } from '../../../../../Roc.SharedClientApp/types/model-status';
import * as Constants from '../../../../../Roc.SharedClientApp/util/Constants';
import RocTitle from '../RocTitle';
import CalendarCheckmarkSVG from '../svg/CalendarCheckmarkSVG';
import CalendarPowerSVG from '../svg/CalendarPowerSVG';
import CalendarTrashSVG from '../svg/CalendarTrashSVG';
import CheckCircleSVG from '../svg/CheckCircleSVG';
import DashCircleSVG from '../svg/DashCircleSVG';
import FolderSVG from '../svg/FolderSVG';
import GarbageCanCircleSVG from '../svg/GarbageCanCircleSVG';
import InfoSVG from '../svg/InfoSVG';
import PencilWriteSVG from '../svg/PencilWriteSVG';
import EditStatusLabel from './EditStatusLabel';
import withEditContext from './withEditContext';

export interface EditControlsProps {
	editContext: EditContext;
	record: BaseDto;
	popoverPlacement?: Popper.Placement;
}

export interface EditControlsState {
	popoverOpen: boolean;
}

interface DefaultPopoverStatusOnEditProps {
	editContext: EditContext;
	activeVersion: any;
	isDraft: boolean | null;
	isFutureVersion: boolean | null;
	isArchived: boolean | null;
	record: {
		versionNo?: number;
		createDate?: Date;
		createUser?: string;
		modifyDate?: Date;
		modifyUser?: string;
		status: ModelStatus;
	};
}

/**
 * Default popover status info to be displayed within the PopoverBody of the EditStatusDisplay icon. Can be overriden by setting
 * statusInfoOverrideOnEdit in the entityDefinition.
 */
function DefaultPopoverStatusOnEdit(props: DefaultPopoverStatusOnEditProps) {
	const { record, editContext, activeVersion, isDraft, isFutureVersion, isArchived } = props;

	const statusName = (prefix: string, modelStatus: ModelStatus) => {
		switch (modelStatus) {
			case ModelStatus.Published:
				return `${prefix} Publish`;

			case ModelStatus.Deleted:
				return `${prefix} Deletion`;

			case ModelStatus.Draft:
				return `${prefix} Draft`;

			case ModelStatus.Inactive:
				return `${prefix} Inactivation`;

			default:
				return `${prefix} ${modelStatus}`;
		}
	};

	const statusLabel = editContext.entityDefinition.statusLabel;
	const statusField = editContext.entityDefinition.statusField;
	const versionLabel = editContext.entityDefinition.versionLabel
		? editContext.entityDefinition.versionLabel
		: 'Version';

	return (
		<>
			{activeVersion && isDraft ? (
				<>
					<EditStatusLabel label={statusLabel} value={record.status} />
					<EditStatusLabel label={versionLabel} value={activeVersion.versionNo} />
					<EditStatusLabel
						label="Created By"
						value={activeVersion.createUser ? activeVersion.createUser : Constants.systemUserName}
					/>
					{activeVersion.createDate ? (
						<EditStatusLabel label="Created On" value={formatDateTime(activeVersion.createDate)} />
					) : null}
					{!moment(activeVersion.createDate).isSame(moment(activeVersion.modifyDate)) ? (
						<>
							<EditStatusLabel
								label="Modified By"
								value={activeVersion.modifyUser ? activeVersion.modifyUser : Constants.systemUserName}
							/>
							<EditStatusLabel label="Modified On" value={formatDateTime(activeVersion.modifyDate)} />
						</>
					) : null}
				</>
			) : activeVersion && isFutureVersion ? (
				<>
					{record.status ? (
						<EditStatusLabel label={statusLabel} value={statusName('Future', record.status)} />
					) : null}
					<EditStatusLabel label={versionLabel} value={activeVersion.versionNo} />
					<EditStatusLabel
						label="Originally Created By"
						value={record.createUser ? record.createUser : Constants.systemUserName}
					/>
					{record.createDate ? (
						<EditStatusLabel label="Originally Created On" value={formatDateTime(record.createDate)} />
					) : null}
					<EditStatusLabel label="Scheduled By" value={activeVersion.createUser} />
					{activeVersion.scheduledTime ? (
						<EditStatusLabel label="Scheduled For" value={formatDateTime(activeVersion.scheduledTime)} />
					) : null}
				</>
			) : activeVersion && isArchived ? (
				<>
					{record.status ? (
						<EditStatusLabel label={statusLabel} value={statusName('Archived', record.status)} />
					) : null}
					<EditStatusLabel label={versionLabel} value={activeVersion.versionNo} />
					<EditStatusLabel
						label="Originally Created By"
						value={record.createUser ? record.createUser : Constants.systemUserName}
					/>
					{record.createDate ? (
						<EditStatusLabel label="Originally Created On" value={formatDateTime(record.createDate)} />
					) : null}
					<EditStatusLabel label="Archived By" value={activeVersion.createUser} />
					{activeVersion.processTime ? (
						<EditStatusLabel label="Archived On" value={formatDateTime(activeVersion.processTime)} />
					) : null}
				</>
			) : (
				// non-archived, non-future, non-draft version
				<>
					<EditStatusLabel label={statusLabel} value={record[statusField]} />
					<EditStatusLabel label={versionLabel} value={record.versionNo} />
					<EditStatusLabel
						label="Created By"
						value={record.createUser ? record.createUser : Constants.systemUserName}
					/>
					{record.createDate ? (
						<EditStatusLabel label="Created On" value={formatDateTime(record.createDate)} />
					) : null}
					{!moment(record.createDate).isSame(moment(record.modifyDate)) ? (
						<>
							<EditStatusLabel
								label="Modified By"
								value={record.modifyUser ? record.modifyUser : Constants.systemUserName}
							/>
							<EditStatusLabel label="Modified On" value={formatDateTime(record.modifyDate)} />
						</>
					) : null}
				</>
			)}
		</>
	);
}

/**
 * Component that renders the actions for any particular field.
 * These include edit, delete, info, and future options for each individual record.
 *
 * @class EditControls
 * @extends {React.Component<EditControlsProps<any>>}
 */
class EditStatusDisplay extends React.Component<EditControlsProps, EditControlsState> {
	private archivedContainerRef: React.RefObject<HTMLDivElement>;
	private container;

	public static defaultProps = {
		popoverPlacement: 'bottom',
	};

	constructor(props) {
		super(props);

		this.archivedContainerRef = React.createRef();
		this.container = React.createRef();

		this.state = {
			popoverOpen: false,
		};
	}

	public handleButtonClick = (e) => {
		// prevent form submit
		e.preventDefault();

		this.toggle();
	};

	public toggle = () => {
		this.setState({
			popoverOpen: !this.state.popoverOpen,
		});
	};

	public render() {
		const { record, editContext } = this.props;
		const isBasicModelOnly = editContext.entityDefinition.isBasicModel && !editContext.entityDefinition.isBaseModel;

		const activeVersion = editContext.getActiveVersion();
		const isArchived = activeVersion && activeVersion.type === EntityVersionType.Archived;
		const isFutureVersion = activeVersion && activeVersion.type === EntityVersionType.Future;
		const isDraft = activeVersion && activeVersion.type === EntityVersionType.Draft;

		const listingItemStatusIconClass = 'listing-item-status-icon';

		return (
			<>
				{isArchived ? (
					<RocTitle label="Archived Record">
						<div>
							<FolderSVG class={'listing-item-status-icon__left'} />
							<span className="visually-hidden">Archived Record</span>
						</div>
					</RocTitle>
				) : null}{' '}
				<span ref={this.container} className="listing-status-display">
					<button
						className="listing-status-btn"
						id={'Popover-' + record.id}
						onClick={this.handleButtonClick}
						type="button"
						aria-expanded={this.state.popoverOpen}
					>
						<span className="visually-hidden">
							Click to view {editContext.entityDefinition.singularName} status
						</span>
						{editContext.entityDefinition.statusIconOverride ? (
							editContext.entityDefinition.statusIconOverride(record)
						) : isBasicModelOnly ? (
							<InfoSVG class={listingItemStatusIconClass} testId="listing-status-info-icon" />
						) : record.status === ModelStatus.Published ? (
							<>
								{isFutureVersion ? (
									<CalendarCheckmarkSVG
										class={listingItemStatusIconClass}
										testId="listing-status-calendar-checkmark-icon"
									/>
								) : (
									<CheckCircleSVG
										class={listingItemStatusIconClass}
										testId="listing-status-circle-check-icon"
									/>
								)}
							</>
						) : record.status === ModelStatus.Deleted ? (
							<>
								{isFutureVersion ? (
									<CalendarTrashSVG
										class={listingItemStatusIconClass}
										testId="listing-status-calendar-trash-icon"
									/>
								) : (
									<GarbageCanCircleSVG
										class={listingItemStatusIconClass}
										testId="listing-status-garbage-can-circle-icon"
									/>
								)}
							</>
						) : record.status === ModelStatus.Inactive ? (
							<>
								{isFutureVersion ? (
									<CalendarPowerSVG
										class={listingItemStatusIconClass}
										testId="listing-status-calendar-power-icon"
									/>
								) : (
									<DashCircleSVG
										class={listingItemStatusIconClass}
										testId="listing-status-dash-icon"
									/>
								)}
							</>
						) : record.status === ModelStatus.Draft ? (
							<PencilWriteSVG class={listingItemStatusIconClass} testId="listing-status-draft-icon" />
						) : (
							<InfoSVG class={listingItemStatusIconClass} testId="listing-status-info-icon" />
						)}
					</button>
					<Popover
						target={'Popover-' + record.id}
						placement={this.props.popoverPlacement}
						isOpen={this.state.popoverOpen}
						toggle={this.toggle}
						className="popover-wider"
						container={this.container}
					>
						<PopoverBody>
							{!editContext.entityDefinition.statusInfoOverrideOnEdit ? (
								<DefaultPopoverStatusOnEdit
									record={record}
									editContext={editContext}
									activeVersion={activeVersion}
									isDraft={isDraft}
									isArchived={isArchived}
									isFutureVersion={isFutureVersion}
								/>
							) : (
								editContext.entityDefinition.statusInfoOverrideOnEdit(record)
							)}
						</PopoverBody>
					</Popover>
				</span>
			</>
		);
	}
}

export default withEditContext(EditStatusDisplay);
